import { createTheme } from "@mui/material/styles";

/* ---------------------------------------------------------------------------
   BRAND — single source of truth for the MUI side of the theme.

   These win over the broker's API colours on purpose. App.js already pipes
   allColors into createDarkTheme/createLightTheme, and the broker record for
   ATHA24102025105924 carries internalButtonColor #284CCF — the cobalt this
   re-brand is moving away from. Taking the value from BRAND instead keeps
   Material-UI in step with src/theme-minimal.css, which pins the same accent
   on the CSS-variable side.

   Violet is deliberate: on a trading screen red means sell and green means
   buy, so the action colour has to sit outside both bands.

   Everything else in these factories still reads from allColors — only the
   accent, the type family and the type scale are pinned here.
   --------------------------------------------------------------------------- */
const BRAND = {
  dark:  { accent: "#7a68dd" },
  light: { accent: "#5b4bc4" },
  fontUi:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSize: 14,
  fontStyle: "normal",
};


// === Light Theme (Static - No Changes) ===
// export const lightTheme = createTheme({
//   palette: {
//     mode: "light",
//     primary: {
//       main: "#464342",
//     },
//     background: {
//       default: "#f5f5f5",
//       paper: "rgb(222, 222, 222)",
//     },
//     text: {
//       primary: "#000000",
//       secondary: "#455a64",
//     },
//   },
//   components: {
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#ffffff",
//           color: "#000000",
//         },
//       },
//     },
//     MuiTableHead: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e0e0e0",
//         },
//       },
//     },
//     MuiTableCell: {
//       styleOverrides: {
//         head: {
//           color: "#000000",
//           fontWeight: "bold",
//         },
//       },
//     },
//     MuiToggleButtonGroup: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e0e0e0",
//           borderRadius: 8,
//           padding: 4,
//           width: "fit-content",
//         },
//       },
//     },
//     MuiCssBaseline: {
//       styleOverrides: {
//         body: {
//           "&::-webkit-scrollbar": {
//             width: "8px",
//             height: "8px",
//           },
//           "&::-webkit-scrollbar-track": {
//             background: "#1e1e1e",
//           },
//           "&::-webkit-scrollbar-thumb": {
//             backgroundColor: "#888",
//             borderRadius: "10px",
//             border: "2px solid transparent",
//             backgroundClip: "content-box",
//           },
//           "&::-webkit-scrollbar-thumb:hover": {
//             backgroundColor: "#555",
//           },
//         },
//       },
//     },
//     MuiToggleButton: {
//       styleOverrides: {
//         root: ({ theme }) => ({
//           fontWeight: "bold",
//           paddingLeft: theme.spacing(4),
//           paddingRight: theme.spacing(4),
//           borderRadius: 8,
//           "&.Mui-selected": {
//             color: "#fff",
//             backgroundColor: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: (ownerState) =>
//                 ownerState.value === "BUY" ? "#00a543" : "#b71c1c",
//             },
//           },
//           "&:not(.Mui-selected)": {
//             color: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: "#e0e0e0",
//             },
//           },
//         }),
//       },
//     },
//     MuiDialogTitle: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e4e0e0",
//           color: "#000",
//           fontWeight: "bold",
//         },
//       },
//     },
//   },
// });

// === Dynamic Dark Theme (Uses API Colors) ===
export const createDarkTheme = (allColors) => {
  // Fallback to default dark theme colors if API values are null
  const defaults = {
    navbarColor: "#000",
    darkBackgroundColor: "#000",
    backgroundColor1: "#121212",
    backgroundColor2: "#1e1e1e",
    buttonColor: "#90caf9",
    fontColor: "#ffffff",
    popupHeaderColor: "#383839",
    popupBodyColor: "#1e1e1e",
    tableHeaderColor: "#202023",
    tableBodyColor: "#24262d",
  };

  // Merge API colors with defaults
  const colors = {
    navbarColor: allColors?.navbarColor || defaults.navbarColor,
    darkBackgroundColor:
      allColors?.darkBackgroundColor || defaults.darkBackgroundColor,
    backgroundColor1: allColors?.backgroundColor1 || defaults.backgroundColor1,
    backgroundColor2: allColors?.backgroundColor2 || defaults.backgroundColor2,
    buttonColor: BRAND.dark.accent,
    fontColor: allColors?.fontColor || defaults.fontColor,
    popupHeaderColor: allColors?.popupHeaderColor || defaults.popupHeaderColor,
    popupBodyColor: allColors?.popupBodyColor || defaults.popupBodyColor,
    tableHeaderColor: allColors?.tableHeaderColor || defaults.tableHeaderColor,
    tableBodyColor: allColors?.tableBodyColor || defaults.tableBodyColor,
  };

  return createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: colors.buttonColor,
      },
      background: {
        default: colors.darkBackgroundColor,
        paper: "#1e1e1e",
      },
      text: {
        primary: colors.fontColor,
        secondary: colors.fontColor,
      },
    },
    typography: {
      fontFamily: BRAND.fontUi,
      fontSize: colors.fontSize,
      fontStyle: colors.fontStyle,
      allVariants: {
        color: colors.fontColor,
        fontStyle: colors.fontStyle,
      },
      h1: {
        fontSize: `${BRAND.fontSize * 2.4}px`,

        color: colors.fontColor,
      },
      h2: {
        fontSize: `${BRAND.fontSize * 2}px`,

        color: colors.fontColor,
      },
      h3: {
        fontSize: `${BRAND.fontSize * 1.8}px`,

        color: colors.fontColor,
      },
      h4: {
        fontSize: `${BRAND.fontSize * 1.5}px`,

        color: colors.fontColor,
      },
      h5: {
        fontSize: `${BRAND.fontSize * 1.3}px`,

        color: colors.fontColor,
      },
      h6: {
        fontSize: `${BRAND.fontSize * 1.1}px`,

        color: colors.fontColor,
      },
      subtitle1: {
        fontSize: `${BRAND.fontSize * 1}px`,
        color: colors.fontColor,
      },
      subtitle2: {
        fontSize: `${BRAND.fontSize * 0.9}px`,
        color: colors.fontColor,
      },
      body1: {
        fontSize: `${BRAND.fontSize}px`,
        color: colors.fontColor,
      },
      body2: {
        fontSize: `${BRAND.fontSize * 0.9}px`,
        color: colors.fontColor,
      },
      button: {
        textTransform: "none",

        fontSize: `${BRAND.fontSize}px`,
        color: colors.fontColor,
      },
      caption: {
        fontSize: `${BRAND.fontSize * 0.8}px`,
        color: colors.fontColor,
      },
      overline: {
        fontSize: `${BRAND.fontSize * 0.75}px`,
        color: colors.fontColor,
        textTransform: "uppercase",
      },
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.popupBodyColor,
            backgroundImage: "none", // Remove any gradient
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.darkBackgroundColor,
            color: colors.fontColor,
            fontStyle: allColors?.fontStyle,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.tableHeaderColor,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            // color: colors.fontColor,
            fontWeight: "bold",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            backgroundColor: `color-mix(in srgb, ${colors.darkBackgroundColor} 80%, white 20%)`,
            color: colors.fontColor,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent", // Changed to transparent
            paddingTop: 0, // Remove gap between title and content
            borderTop: "none", // Remove any border
            borderBottom: "none", // Remove any border
            // "&::-webkit-scrollbar": {
            //   width: "8px",
            //   height: "8px",
            // },
            // "&::-webkit-scrollbar-track": {
            //   background: colors.darkBackgroundColor,
            // },
            // "&::-webkit-scrollbar-thumb": {
            //   backgroundColor: colors.darkBackgroundColor,
            //   borderRadius: "10px",
            //   border: "2px solid transparent",
            //   backgroundClip: "content-box",
            // },
            // "&::-webkit-scrollbar-thumb:hover": {
            //   backgroundColor: colors.darkBackgroundColor,
            // },
          },
          dividers: {
            borderTop: "none",
            borderBottom: "none",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: colors?.popupHeaderColor, // Changed to transparent
            color: colors.fontColor,
            fontWeight: "bold",
            borderBottom: "none", // Remove bottom border
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent", // Add this to make actions transparent
            borderTop: "none", // Remove top border
            paddingTop: 0, // Remove gap between content and actions
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: colors.darkBackgroundColor,
            borderRadius: 8,
            padding: 4,
            width: "fit-content",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            fontWeight: "bold",
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
            borderRadius: 8,
            "&.Mui-selected": {
              color: "#fff",
              backgroundColor:
                ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
              "&:hover": {
                backgroundColor:
                  ownerState.value === "BUY" ? "#00a543" : "#b71c1c",
              },
            },
            "&:not(.Mui-selected)": {
              color: ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
              // "&:hover": {
              //   backgroundColor: "#2c2c3a",
              // },
            },
          }),
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontWeight: `${BRAND.fontStyle} !important`,
            fontStyle: `${BRAND.fontStyle} !important`,
            fontFamily: `${BRAND.fontUi} !important`,
            "*": {
              fontWeight: `${BRAND.fontStyle} !important`, // enforce all text bold
              fontStyle: `${BRAND.fontStyle} !important`,
              fontFamily: `${BRAND.fontUi} !important`,
            },
            // "&::-webkit-scrollbar": {
            //   width: "8px",
            //   height: "8px",
            // },
            // "&::-webkit-scrollbar-track": {
            //   background: colors.darkBackgroundColor,
            // },
            // "&::-webkit-scrollbar-thumb": {
            //   backgroundColor: colors.darkBackgroundColor,
            //   borderRadius: "10px",
            //   border: "2px solid transparent",
            //   backgroundClip: "content-box",
            // },
            // "&::-webkit-scrollbar-thumb:hover": {
            //   backgroundColor: colors.darkBackgroundColor,
            // },
          },
        },
      },
    },
  });
};

// Static export for dark theme with default colors (for backwards compatibility)
export const darkTheme = createDarkTheme({});

export const createLightTheme = (allColors) => {
  const defaults = {
    navbarColor: "#fff",
    lightBackgroundColor: "#ffffff",
    backgroundColor1: "#f5f5f5",
    backgroundColor2: "rgb(222, 222, 222)",
    buttonColor: "#464342",
    fontColor: "#000000",
    popupHeaderColor: "#e4e0e0",
    popupBodyColor: "#ffffff",
    tableHeaderColor: "#e0e0e0",
    tableBodyColor: "#ffffff",
  };

  const colors = {
    navbarColor: allColors?.navbarColor || defaults.navbarColor,
    lightBackgroundColor:
      allColors?.lightBackgroundColor || defaults.lightBackgroundColor,
    backgroundColor1: allColors?.backgroundColor1 || defaults.backgroundColor1,
    backgroundColor2: allColors?.backgroundColor2 || defaults.backgroundColor2,
    buttonColor: BRAND.light.accent,
    fontColor: allColors?.lightFontColor || defaults.fontColor,
    popupHeaderColor:
      allColors?.lightPopupHeaderColor || defaults.popupHeaderColor,
    popupBodyColor: allColors?.lightPopupBodyColor || defaults.popupBodyColor,
    tableHeaderColor:
      allColors?.lightTableHeaderColor || defaults.tableHeaderColor,
    tableBodyColor: allColors?.lightTableBodyColor || defaults.tableBodyColor,
  };

  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: colors.buttonColor,
      },
      background: {
        default: colors.lightBackgroundColor,
        paper: "rgb(222, 222, 222)",
      },
      text: {
        primary: colors.fontColor,
        secondary: colors.fontColor,
      },
    },

    typography: {
      fontFamily: BRAND.fontUi,
      fontSize: allColors?.lightFontSize || 14,
      fontStyle: allColors?.lightFontStyle,
      fontColor: colors?.fontColor,
      allVariants: {
        color: colors.fontColor,
        fontStyle: colors.lightFontStyle,
      },
      h1: {
        fontSize: `${BRAND.fontSize * 2.4}px`,

        color: colors.fontColor,
      },
      h2: {
        fontSize: `${BRAND.fontSize * 2}px`,

        color: colors.fontColor,
      },
      h3: {
        fontSize: `${BRAND.fontSize * 1.8}px`,

        color: colors.fontColor,
      },
      h4: {
        fontSize: `${BRAND.fontSize * 1.5}px`,

        color: colors.fontColor,
      },
      h5: {
        fontSize: `${BRAND.fontSize * 1.3}px`,

        color: colors.fontColor,
      },
      h6: {
        fontSize: `${BRAND.fontSize * 1.1}px`,

        color: colors.fontColor,
      },
      subtitle1: {
        fontSize: `${BRAND.fontSize * 1}px`,
        color: colors.fontColor,
      },
      subtitle2: {
        fontSize: `${BRAND.fontSize * 0.9}px`,
        color: colors.fontColor,
      },
      body1: {
        fontSize: `${BRAND.fontSize}px`,
        color: colors.fontColor,
      },
      body2: {
        fontSize: `${BRAND.fontSize * 0.9}px`,
        color: colors.fontColor,
      },
      button: {
        textTransform: "none",

        fontSize: `${BRAND.fontSize}px`,
        color: colors.fontColor,
      },
      caption: {
        fontSize: `${BRAND.fontSize * 0.8}px`,
        color: colors.fontColor,
      },
      overline: {
        fontSize: `${BRAND.fontSize * 0.75}px`,
        color: colors.fontColor,
        textTransform: "uppercase",
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.lightBackgroundColor,
            color: colors.fontColor,
            fontStyle: allColors?.lightFontStyle,
            fontWeight: allColors?.lightFontStyle,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.tableHeaderColor,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: colors.fontColor,
            fontWeight: "bold",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            backgroundColor: `color-mix(in srgb, ${colors.lightBackgroundColor} 80%, black 20%)`,
            color: colors.lightFontColor,
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: colors.lightBackgroundColor,
            borderRadius: 8,
            padding: 4,
            width: "fit-content",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontWeight: "bold",
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
            borderRadius: 8,
            "&.Mui-selected": {
              color: "#fff",
              backgroundColor: (ownerState) =>
                ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
              "&:hover": {
                backgroundColor: (ownerState) =>
                  ownerState.value === "BUY" ? "#00a543" : "#b71c1c",
              },
            },
            "&:not(.Mui-selected)": {
              color: (ownerState) =>
                ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
              // "&:hover": {
              //   backgroundColor: "#e0e0e0",
              // },
            },
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: colors.popupHeaderColor,
            color: colors.fontColor,
            fontWeight: "bold",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontWeight: `${BRAND.fontStyle} !important`,
            fontStyle: `${BRAND.fontStyle} !important`,
            fontFamily: `${BRAND.fontUi} !important`,
            "*": {
              fontWeight: `${BRAND.fontStyle} !important`, // enforce all text bold
              fontStyle: `${BRAND.fontStyle} !important`,
              fontFamily: `${BRAND.fontUi} !important`,
            },
            // "&::-webkit-scrollbar": {
            //   width: "8px",
            //   height: "8px",
            // },
            // "&::-webkit-scrollbar-track": {
            //   background: "#1e1e1e",
            // },
            // "&::-webkit-scrollbar-thumb": {
            //   backgroundColor: "#888",
            //   borderRadius: "10px",
            //   border: "2px solid transparent",
            //   backgroundClip: "content-box",
            // },
            // "&::-webkit-scrollbar-thumb:hover": {
            //   backgroundColor: "#555",
            // },
          },
        },
      },
    },
  });
};

export const lightTheme = createLightTheme({});

// import { createTheme } from "@mui/material/styles";

// // === Dark Theme ===
// export const darkTheme = createTheme({
//   palette: {
//     mode: "dark",
//     primary: {
//       main: "#90caf9",
//     },
//     background: {
//       default: "#121212",
//       paper: "#1e1e1e",
//     },
//     text: {
//       primary: "#ffffff",
//       secondary: "#b0bec5",
//     },
//   },
//   components: {
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#1e1e1e",
//           color: "#ffffff",
//         },
//       },
//     },
//     MuiTableHead: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#202023",
//         },
//       },
//     },
//     MuiTableCell: {
//       styleOverrides: {
//         head: {
//           color: "#ffffff",
//           fontWeight: "bold",
//         },
//       },
//     },
//     MuiCardContent: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#24262d",
//           color: "#ffffff",
//         },
//       },
//     },
//     MuiDialogContent: {
//       styleOverrides: {
//         root: {
//           "&::-webkit-scrollbar": {
//             width: "8px",
//             height: "8px",
//           },
//           "&::-webkit-scrollbar-track": {
//             background: "#1e1e1e",
//           },
//           "&::-webkit-scrollbar-thumb": {
//             backgroundColor: "#888",
//             borderRadius: "10px",
//             border: "2px solid transparent",
//             backgroundClip: "content-box",
//           },
//           "&::-webkit-scrollbar-thumb:hover": {
//             backgroundColor: "#555",
//           },
//         },
//       },
//     },
//     MuiToggleButtonGroup: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#2c2c3a",
//           borderRadius: 8,
//           padding: 4,
//           width: "fit-content",
//         },
//       },
//     },
//     MuiToggleButton: {
//       styleOverrides: {
//         root: ({ theme }) => ({
//           fontWeight: "bold",
//           paddingLeft: theme.spacing(4),
//           paddingRight: theme.spacing(4),
//           borderRadius: 8,
//           "&.Mui-selected": {
//             color: "#fff",
//             backgroundColor: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: (ownerState) =>
//                 ownerState.value === "BUY" ? "#00a543" : "#b71c1c",
//             },
//           },
//           "&:not(.Mui-selected)": {
//             color: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: "#2c2c3a",
//             },
//           },
//         }),
//       },
//     },
//     MuiDialogTitle: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#383839",
//           color: "#fff",
//           fontWeight: "bold",
//         },
//       },
//     },

//     MuiCssBaseline: {
//       styleOverrides: {
//         body: {
//           "&::-webkit-scrollbar": {
//             width: "8px",
//             height: "8px",
//           },
//           "&::-webkit-scrollbar-track": {
//             background: "#1e1e1e",
//           },
//           "&::-webkit-scrollbar-thumb": {
//             backgroundColor: "#888",
//             borderRadius: "10px",
//             border: "2px solid transparent",
//             backgroundClip: "content-box",
//           },
//           "&::-webkit-scrollbar-thumb:hover": {
//             backgroundColor: "#555",
//           },
//         },
//       },
//     },
//   },
// });

// // === Light Theme ===
// export const lightTheme = createTheme({
//   palette: {
//     mode: "light",
//     primary: {
//       main: "#464342",
//     },
//     background: {
//       default: "#f5f5f5",
//       paper: "rgb(222, 222, 222)",
//     },
//     text: {
//       primary: "#000000",
//       secondary: "#455a64",
//     },
//   },
//   components: {
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#ffffff",
//           color: "#000000",
//         },
//       },
//     },
//     MuiTableHead: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e0e0e0",
//         },
//       },
//     },
//     MuiTableCell: {
//       styleOverrides: {
//         head: {
//           color: "#000000",
//           fontWeight: "bold",
//         },
//       },
//     },
//     MuiToggleButtonGroup: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e0e0e0",
//           borderRadius: 8,
//           padding: 4,
//           width: "fit-content",
//         },
//       },
//     },
//     MuiCssBaseline: {
//       styleOverrides: {
//         body: {
//           "&::-webkit-scrollbar": {
//             width: "8px",
//             height: "8px",
//           },
//           "&::-webkit-scrollbar-track": {
//             background: "#1e1e1e",
//           },
//           "&::-webkit-scrollbar-thumb": {
//             backgroundColor: "#888",
//             borderRadius: "10px",
//             border: "2px solid transparent",
//             backgroundClip: "content-box",
//           },
//           "&::-webkit-scrollbar-thumb:hover": {
//             backgroundColor: "#555",
//           },
//         },
//       },
//     },
//     MuiToggleButton: {
//       styleOverrides: {
//         root: ({ theme }) => ({
//           fontWeight: "bold",
//           paddingLeft: theme.spacing(4),
//           paddingRight: theme.spacing(4),
//           borderRadius: 8,
//           "&.Mui-selected": {
//             color: "#fff",
//             backgroundColor: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: (ownerState) =>
//                 ownerState.value === "BUY" ? "#00a543" : "#b71c1c",
//             },
//           },
//           "&:not(.Mui-selected)": {
//             color: (ownerState) =>
//               ownerState.value === "BUY" ? "#00c853" : "#d32f2f",
//             "&:hover": {
//               backgroundColor: "#e0e0e0",
//             },
//           },
//         }),
//       },
//     },
//     MuiDialogTitle: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#e4e0e0",
//           color: "#000",
//           fontWeight: "bold",
//         },
//       },
//     },
//   },
// });
